import { IconButton, cn } from '@glade-ai/ui';
import { type FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { mixpanelTrack } from '@/providers/Mixpanel';
import { useScreenRecord } from '@/providers/ScreenRecord';

/** Format seconds as mm:ss. */
const formatTime = (totalSeconds: number): string => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/** Floating recording controls bar shown at top-center of viewport during active recording. */
const RecordingOverlay: FC = () => {
  const {
    isRecording,
    isPaused,
    isMicMuted,
    hasMicAccess,
    elapsedSeconds,
    maxDurationSeconds,
    pauseRecording,
    resumeRecording,
    toggleMicMute,
    stopRecording,
  } = useScreenRecord();

  const remainingSeconds = useMemo(
    () => Math.max(0, maxDurationSeconds - elapsedSeconds),
    [maxDurationSeconds, elapsedSeconds],
  );

  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  }, [isPaused, pauseRecording, resumeRecording]);

  const handleMicToggle = useCallback(() => {
    toggleMicMute();
    mixpanelTrack('screen_recording_mic_toggled', { muted: !isMicMuted, elapsedSeconds });
  }, [toggleMicMute, isMicMuted, elapsedSeconds]);

  const handleStop = useCallback(() => {
    mixpanelTrack('screen_recording_stopped', {
      durationSeconds: elapsedSeconds,
      autoStopped: false,
      hasMicAccess,
    });
    stopRecording();
  }, [stopRecording, elapsedSeconds, hasMicAccess]);

  if (!isRecording || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <section
      role='status'
      aria-live='polite'
      aria-label='Screen recording controls'
      className={cn(
        'fixed top-4 start-1/2 -translate-x-1/2 z-[9999]',
        'flex items-center gap-3 rounded-full px-4 py-2',
        'bg-surface shadow-lg',
        'border border-outline',
      )}
    >
      {/* Recording indicator dot */}
      <span role='none' className={cn('size-2.5 rounded-full', isPaused ? 'bg-warning' : 'bg-danger animate-pulse')} />

      {/* Status label */}
      <span className='text-sm font-medium text-on-surface'>{isPaused ? 'Paused' : 'Recording'}</span>

      {/* Elapsed time */}
      <span className='font-mono text-sm tabular-nums text-on-surface'>{formatTime(elapsedSeconds)}</span>

      {/* Divider */}
      <span role='none' className='h-4 w-px bg-outline' />

      {/* Remaining time */}
      <span className='font-mono text-sm tabular-nums text-on-surface-variant'>{formatTime(remainingSeconds)}</span>

      {/* Mic toggle */}
      {hasMicAccess && (
        <IconButton
          icon={isMicMuted ? 'ph--microphone-slash--fill' : 'ph--microphone--fill'}
          label={isMicMuted ? 'Unmute microphone' : 'Mute microphone'}
          iconOnly
          size='sm'
          variant={isMicMuted ? 'ghost-destructive' : 'ghost'}
          onClick={handleMicToggle}
        />
      )}

      {/* Pause / Resume */}
      <IconButton
        icon={isPaused ? 'ph--play--fill' : 'ph--pause--fill'}
        label={isPaused ? 'Resume recording' : 'Pause recording'}
        iconOnly
        size='sm'
        variant='secondary'
        onClick={handlePauseResume}
      />

      {/* Stop */}
      <IconButton
        icon='ph--stop-circle--fill'
        label='Stop recording'
        iconOnly
        size='sm'
        variant='destructive'
        onClick={handleStop}
      />
    </section>,
    document.body,
  );
};

export default RecordingOverlay;
